'use client'

import { useTranslations } from 'next-intl'
import { usePathname, useRouter } from 'next/navigation'
import { useEffect, useRef, useState } from 'react'

import { toast } from '@/lib/toast'
import { useApiBilling, useInvalidateApiBilling } from '@/service/api-billing.hook'

const BASELINE_KEY = 'orderx.creditCheckoutBaseline'
const POLL_MS = 2000
const POLL_TRIES = 15

/** Remember `grantedTotal` before leaving for Stripe, so the return can tell when credits land. */
export function stashCheckoutBaseline(grantedTotal: number) {
  try {
    sessionStorage.setItem(BASELINE_KEY, String(grantedTotal))
  } catch {
    // Storage blocked: the return just shows the "can take a minute" note.
  }
}

function takeCheckoutBaseline(): number | null {
  try {
    const raw = sessionStorage.getItem(BASELINE_KEY)
    sessionStorage.removeItem(BASELINE_KEY)
    return raw === null ? null : Number(raw)
  } catch {
    return null
  }
}

/**
 * Handles the Stripe return (`?credits=success|cancel`). Credits arrive through Stripe's webhook,
 * not the redirect, so a success polls GetApiBilling until `grantedTotal` passes the stashed
 * baseline. The param is stripped on mount so a reload doesn't start it over.
 */
export function useCreditCheckoutReturn(): { processing: boolean } {
  const t = useTranslations('settings.billing.credits.return')
  const router = useRouter()
  const pathname = usePathname()
  const { refetch } = useApiBilling()
  const invalidateAll = useInvalidateApiBilling()
  const [processing, setProcessing] = useState(false)
  const started = useRef(false)
  // Polling outlives dependency changes; only leaving the page stops it.
  const unmounted = useRef(false)
  useEffect(
    () => () => {
      unmounted.current = true
    },
    []
  )

  useEffect(() => {
    if (started.current) return
    const outcome = new URLSearchParams(window.location.search).get('credits')
    if (outcome !== 'success' && outcome !== 'cancel') return
    started.current = true
    router.replace(pathname)
    const baseline = takeCheckoutBaseline()

    if (outcome === 'cancel') {
      toast.info(t('canceled'))
      return
    }
    if (baseline === null) {
      void invalidateAll()
      toast.info(t('slow'))
      return
    }

    setProcessing(true)
    void (async () => {
      for (let attempt = 0; attempt < POLL_TRIES && !unmounted.current; attempt++) {
        const { data } = await refetch()
        if (data && data.grantedTotal > baseline) {
          setProcessing(false)
          void invalidateAll()
          toast.success(t('added'))
          return
        }
        await new Promise((resolve) => setTimeout(resolve, POLL_MS))
      }
      if (unmounted.current) return
      setProcessing(false)
      void invalidateAll()
      toast.info(t('slow'))
    })()
  }, [invalidateAll, pathname, refetch, router, t])

  return { processing }
}
